import { useState, useEffect } from 'react';
import apiService from '../../../services/api';

export interface Order {
  id: string;
  accessionId: string;
  patientId: string;
  collectionDatetime: string;
  specimenClass: string;
  orderingPhysician: string;
  accessionLocationId: string;
  clinicalHistory: string;
  accessionedBy: string;
  accessionedById: string;
  grossingTechId: string;
  assignedPathologistId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  amendedFromId?: number;
  amendedAt?: string;
  amendedBy?: string;
  version?: string;
  addendaId?: string;
  orderedBy?: number;
  orderDate?: string;
  createdBy?: string;
  sectionValues?: string;
}

interface SourceType {
  id?: number;
  part_code: string;
  part_type: string;
  specimen_part_id?: number;
  collection_method: string;
  collection_datetime: string;
  site: string;
  condition: string;
  submitted_by: string;
  cpt_code: string;
}

interface UserWithRoles {
  id: number;
  name: string;
  email: string;
  roles?: { id: number; name: string; description?: string }[];
}

export const useOrderDialog = (onOrderSaved?: () => void) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [isAddendumMode, setIsAddendumMode] = useState(false);
  const [isAmendMode, setIsAmendMode] = useState(false);
  const [amendLoading, setAmendLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserWithRoles | null>(null);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [versionHistory, setVersionHistory] = useState<Order[]>([]);
  const [versionHistoryLoading, setVersionHistoryLoading] = useState(false);

  const isPathologist: boolean = !!(
    currentUser && 
    Array.isArray(currentUser.roles) && 
    currentUser.roles.some((r: { name?: string }) => r && r.name && r.name.toLowerCase() === 'pathologist')
  );

  const handleEdit = async (order: Order) => {
    setEditingOrder(order);
    setIsAddendumMode(false);
    setIsAmendMode(false);
    setOpenDialog(true);
  };

  const handleAmend = async (order: Order) => {
    if (!isPathologist) return;
    
    setAmendLoading(true);
    let newVersion = '2';
    if (order.version) {
      const majorVersion = parseInt(order.version.split('.')[0]);
      newVersion = String(majorVersion + 1);
    }
    
    const newEditingOrder = { ...order, version: newVersion, status: 'Amended' };
    setEditingOrder(newEditingOrder);
    setIsAmendMode(true);
    setIsAddendumMode(false);
    setOpenDialog(true);
    setAmendLoading(false);
  };

  const handleAddendum = (order: Order) => {
    setEditingOrder(order);
    setIsAddendumMode(true);
    setIsAmendMode(false);
    setOpenDialog(true);
  };

  const handleArchive = async (order: Order) => {
    try {
      await apiService.updateOrder(order.id, { ...order, status: 'Archived' });
      if (onOrderSaved) {
        onOrderSaved();
      }
    } catch (error) {
      console.error('Error archiving order:', error);
    }
  };

  const handleViewVersionHistory = async (order: Order) => {
    setVersionHistoryLoading(true);
    try {
      const data = await apiService.getOrderVersions(order.accessionId.toString());
      setVersionHistory(data.versions || []);
      setVersionHistoryOpen(true);
    } catch (error) {
      console.error('Error fetching version history:', error);
      alert('Failed to fetch version history');
      setVersionHistory([]);
    } finally {
      setVersionHistoryLoading(false);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingOrder(null);
    setIsAddendumMode(false);
    setIsAmendMode(false);
  };

  const handleFinalize = async (orderId: string) => {
    try {
      await apiService.updateOrder(orderId, { status: 'Completed / Finalized' });
      setOpenDialog(false);
      return true;
    } catch (error) {
      console.error('Error finalizing order:', error);
      return false;
    }
  };

  const handleSave = async (orderData: any) => {
    try {
      let createdOrderId: string | null = null;
      
      if (isAmendMode && editingOrder) {
        // Amendment: Increment major version (left of decimal)
        let newVersion = '2';
        if (editingOrder.version) {
          const [major] = editingOrder.version.split('.');
          newVersion = String(parseInt(major) + 1);
        }
        
        // Prepare amended order payload with proper field mapping
        const {
          id, createdAt, amendedAt, amendedBy, amendedFromId, version, ...orderDataToSave
        } = editingOrder;
        
        const amendedOrder = {
          ...orderDataToSave,
          ...orderData,
          status: 'Amended',
          accessionId: editingOrder.accessionId,
          amendedFromId: editingOrder.id,
          amendedAt: new Date().toISOString(),
          amendedBy: currentUser?.id?.toString(),
          version: newVersion
        };
        
        const result = await apiService.createOrder(amendedOrder);
        createdOrderId = result.id;
      } else if (isAddendumMode && editingOrder) {
        // Addendum: Increment minor version (right of decimal)
        let newVersion = '1.1';
        if (editingOrder.version) {
          const [major, minor] = editingOrder.version.split('.');
          const majorNum = parseInt(major);
          const minorNum = minor ? parseInt(minor) : 0;
          newVersion = `${majorNum}.${minorNum + 1}`;
        }
        
        // Create addendum record first
        const addendumData = {
          reason: orderData.reason || '',
          partAffected: orderData.part_affected || '',
          comment: orderData.comment || '',
          createdBy: currentUser?.id || 0
        };
        
        const addendumResult = await apiService.createAddendum(addendumData);
        
        // Update the original order with new version and addendaId
        const updatedOrderData = {
          ...orderData,
          version: newVersion,
          addendaId: addendumResult.id
        };
        
        await apiService.updateOrder(editingOrder.id.toString(), updatedOrderData);
        createdOrderId = editingOrder.id;
      } else if (editingOrder) {
        // Update existing order
        await apiService.updateOrder(editingOrder.id.toString(), orderData);
        createdOrderId = editingOrder.id;
      } else if (orderData.autoCreatedOrderId) {
        // Update auto-created order instead of creating a new one
        const { autoCreatedOrderId, ...orderDataWithoutAutoId } = orderData;
        await apiService.updateOrder(autoCreatedOrderId.toString(), orderDataWithoutAutoId);
        createdOrderId = autoCreatedOrderId;
      } else {
        // Create new order with default version "1"
        const result = await apiService.createOrder({
          ...orderData,
          version: "1"
        });
        createdOrderId = result.id;
      }
      
      setOpenDialog(false);
      
      // Call the refresh callback if provided
      if (onOrderSaved) {
        onOrderSaved();
      }
      
      return { success: true, orderId: createdOrderId };
    } catch (error) {
      console.error('Error saving order:', error);
      return { success: false, orderId: null };
    }
  };

  // Fetch current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const data = await apiService.getUserMe();
        setCurrentUser(data);
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };
    fetchCurrentUser();
  }, []);

  return {
    // State
    openDialog,
    editingOrder,
    isAddendumMode,
    isAmendMode,
    amendLoading,
    isPathologist,
    versionHistoryOpen,
    versionHistory,
    versionHistoryLoading,
    
    // Actions
    handleEdit,
    handleAmend,
    handleAddendum,
    handleArchive,
    handleViewVersionHistory,
    handleDialogClose,
    handleFinalize,
    handleSave,
    setVersionHistoryOpen,
  };
}; 